import { database, dbRegistros } from '@/firebase/config'
import { BitacoraData, ICareerResponse, IInstitutionConfiguration, IMantenimiento } from '@shared/types/base'
import { ICertificate } from '@shared/types/certificate'
import { IDegree } from '@shared/types/degree'
import { IDegreePackage, ICertificatePackage } from '@shared/types/package'
import { get, onValue, ref as dbRef } from 'firebase/database'

import { apiService } from '@/services/apiService'
import { EUserRole } from '@shared/types/enums'
import { doc, getDoc, onSnapshot } from 'firebase/firestore'
import { ContractDateState } from '@/store/types'

export const getInstitutionConfiguration = async (institutionId: string): Promise<IInstitutionConfiguration> => {
	const snapshot = await get(dbRef(database, `institutions/${institutionId}/config`))
	return snapshot.val() || {}
}
export const getInstitutionConfigurationTest = async (institutionId: string): Promise<IInstitutionConfiguration> => {
	const snapshot = await get(dbRef(database, `institutions/${institutionId}/testMode/config`))
	return snapshot.val() || {}
}
export const getInstitutionName = async (institutionId: string): Promise<string> => {
	const snapshot = await get(dbRef(database, `institutions/${institutionId}/nombreInstitucion`))
	return snapshot.val() || 'Institución'
}
/**
 * @description Verifica si una institución existe leyendo solo un campo hoja.
 * No usar `institutions/${id}`: descarga el nodo completo (cola, paquetes, etc.) y puede tardar varios segundos.
 */
export const existsInstitution = async (institutionId: string): Promise<boolean> => {
	const snapshot = await get(dbRef(database, `institutions/${institutionId}/nombreInstitucion`))
	return snapshot.exists()
}
export const hasAdminRole = async (userId: string): Promise<boolean> => {
	const snapshot = await get(dbRef(database, `users/${userId}/role/tipoRole`))
	return snapshot.val() === EUserRole.ADMINISTRADOR
}
export const getInstitutionCounter = async (): Promise<number> => {
	const snapshot = await get(dbRef(database, 'institutions/institutionsCounter'))
	return snapshot.val()
}
export const getIdNombreInstitucion = async (institutionId: string): Promise<number> => {
	const snapshot = await get(dbRef(database, `institutions/${institutionId}/idNombreInstitucion`))
	return snapshot.val() || 0
}
export const getInstitutionPackageById = async (
	institutionId: string,
	packageId: number,
	type: 'degrees' | 'certificates',
	environment: string
): Promise<IDegreePackage | ICertificatePackage | null> => {
	const path = environment === 'production' ? `/${type}/packages/${packageId}` : `/testMode/${type}/packages/${packageId}`
	const snapshot = await get(dbRef(database, `institutions/${institutionId}${path}`))
	return snapshot.val() || null
}
export const getInstitutionCertificateById = async (institutionId: string, certificateId: string, environment: string): Promise<ICertificate | null> => {
	const path = environment === 'production' ? `/certificates/queue/${certificateId}` : `/testMode/certificates/queue/${certificateId}`
	const snapshot = await get(dbRef(database, `institutions/${institutionId}${path}`))
	return snapshot.val() || null
}
export const getInstitutionDegreeById = async (institutionId: string, degreeId: string, environment: string): Promise<IDegree | null> => {
	const path = environment === 'production' ? `/degrees/queue/${degreeId}` : `/testMode/degrees/queue/${degreeId}`
	const snapshot = await get(dbRef(database, `institutions/${institutionId}${path}`))
	return snapshot.val() || null
}
export const getUserInstitutions = async (userId: string): Promise<string[]> => {
	const snapshot = await get(dbRef(database, `users/${userId}/institutions`))
	return snapshot.val() || []
}
export const updateUserInstitutionAccess = async (userId: string, institutionId: string, hasAccess: boolean): Promise<void> => {
	try {
		await apiService.post('updateUserInstitutionAccess', { userId, institutionId, hasAccess })
	} catch (error: unknown) {
		const message = error instanceof Error ? error.message : 'Error al actualizar acceso a institución'
		throw new Error(message)
	}
}
export const getMantenimiento = async (): Promise<IMantenimiento | null> => {
	const snapshot = await get(dbRef(database, `/appConfig/mantenimiento`))
	if (!snapshot.exists()) return null
	const value = snapshot.val()
	if (!value || typeof value !== 'object') return null
	return value as IMantenimiento
}

export const listenMantenimiento = (onChange: (mantenimiento: IMantenimiento | null) => void): (() => void) => {
	const mantenimientoRef = dbRef(database, '/appConfig/mantenimiento')
	return onValue(
		mantenimientoRef,
		snapshot => {
			if (!snapshot.exists()) return onChange(null)
			const value = snapshot.val()
			if (!value || typeof value !== 'object') return onChange(null)
			return onChange(value as IMantenimiento)
		},
		error => {
			console.error('Error escuchando mantenimiento:', error)
			onChange(null)
		}
	)
}
/**
 * Escucha cambios en las fechas de contrato de la bitácora de una institución (Firestore).
 * Las fechas se actualizan desde bitacora/{idInstitution}; al cambiar institución hay que re-suscribirse.
 */
export const listenBitacoraContractDate = (idInstitution: string, onChange: (contractDate: ContractDateState | null) => void): (() => void) => {
	if (!idInstitution) {
		onChange(null)
		return () => {
			console.log('No se proporcionó ID de institución')
		}
	}
	const bitacoraRef = doc(dbRegistros, 'bitacora', idInstitution)
	return onSnapshot(
		bitacoraRef,
		snapshot => {
			if (!snapshot.exists()) return onChange(null)
			const data = snapshot.data() as BitacoraData
			onChange({
				fechaInicioContrato: data?.fechaInicioContrato ?? null,
				fechaFinContrato: data?.fechaFinContrato ?? null
			})
		},
		error => {
			console.error('Error escuchando bitácora (fechas contrato):', error)
			onChange(null)
		}
	)
}

export const getInstitutionCarreras = async (institutionId: string): Promise<{ [key: string]: ICareerResponse }> => {
	const snapshot = await get(dbRef(database, `institutions/${institutionId}/Carrera`))
	return snapshot.val() || {}
}

export const obtenerBitacora = async (idInstitution: string): Promise<BitacoraData | null> => {
	try {
		if (!idInstitution) {
			console.warn('No se proporcionó ID de institución')
			return null
		}

		const bitacoraRef = doc(dbRegistros, 'bitacora', idInstitution)
		const bitacoraSnap = await getDoc(bitacoraRef)

		if (!bitacoraSnap.exists()) {
			return null
		}
		return bitacoraSnap.data() as BitacoraData
	} catch (err: any) {
		console.error('Error al obtener bitácora:', err)
		return null
	}
}
export const normalizarFecha = (fecha: string) => {
	if (typeof fecha === 'string' && fecha.includes('/')) {
		const partes = fecha.split('/')
		if (partes.length === 3) {
			// Si el formato es d/m/yyyy o dd/mm/yyyy
			const dia = partes[0].padStart(2, '0')
			const mes = partes[1].padStart(2, '0')
			let anio = partes[2]

			// Manejar años de 2 dígitos (asumiendo años 2000+)
			if (anio.length === 2) {
				anio = '20' + anio
			}

			return `${anio}-${mes}-${dia}`
		}
	}
	return fecha
}

/** Fecha calendario local como `YYYY-MM-DDT00:00:00` (sin conversión a UTC). */
export const toFechaLocalMidnight = (value: string | number | Date): string => {
	const d = new Date(value)
	const y = d.getFullYear()
	const m = String(d.getMonth() + 1).padStart(2, '0')
	const day = String(d.getDate()).padStart(2, '0')
	return `${y}-${m}-${day}T00:00:00`
}

export const convertFirestoreTimestamp = (timestamp: any): number => {
	// Si ya es un número, retornarlo directamente
	if (typeof timestamp === 'number') {
		return timestamp
	}

	// Si es un objeto con _seconds y _nanoseconds (formato Firestore)
	if (timestamp && typeof timestamp === 'object') {
		if ('_seconds' in timestamp || 'seconds' in timestamp) {
			const seconds = timestamp._seconds || timestamp.seconds || 0
			const nanoseconds = timestamp._nanoseconds || timestamp.nanoseconds || 0
			// Convertir a milisegundos: seconds * 1000 + nanoseconds / 1000000
			return seconds * 1000 + Math.floor(nanoseconds / 1000000)
		}

		// Si es un objeto Timestamp de Firestore (método toMillis)
		if (typeof timestamp.toMillis === 'function') {
			return timestamp.toMillis()
		}

		// Si es un objeto Date
		if (timestamp instanceof Date) {
			return timestamp.getTime()
		}
	}

	// Si es un string, intentar parsearlo
	if (typeof timestamp === 'string') {
		return new Date(timestamp).getTime()
	}

	return 0
}

/**
 * @description Formatea un precio con formato de moneda
 */
export const formatPrice = (value: number | undefined): string => {
	if (value === undefined || value === null) return '$0.00'
	return new Intl.NumberFormat('es-MX', {
		style: 'currency',
		currency: 'MXN'
	}).format(value)
}
/**
 * @description Formatea un número con separadores de miles
 */
export const formatNumber = (value: number | undefined): string => {
	if (value === undefined || value === null) return '0'
	return new Intl.NumberFormat('es-MX').format(value)
}

export const isNearExpiry = (date: any): boolean => {
	if (!date) return false
	const expiryDate = new Date(date)
	const today = new Date()
	const daysUntilExpiry = Math.ceil((expiryDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
	return daysUntilExpiry <= 90 && daysUntilExpiry > 0
}

export const daysUntilExpiry = (date: any): number => {
	if (!date) return 0
	const expiryDate = new Date(date)
	const today = new Date()
	return Math.ceil((expiryDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
}

export const isExpired = (date: any): boolean => {
	if (!date) return false
	const expiryDate = new Date(date)
	const today = new Date()
	return expiryDate < today
}
